import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import post from '../utils/post.js';
import clipboard from '../utils/clipboard.js';
import ConfirmPopup from './ConfirmPopup.js';

function getProfileInitials(value) {
	return String(value || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join('');
}

export default function AppHeader({usrInfo, timerCnt, loading}) {
	const nukiActionCooldownSeconds = 10;
	const nukiDistanceLimitMeters = 20;
	const [showNukiStatusPopup, setShowNukiStatusPopup] = useState(false);
	const [nukiStatusLoading, setNukiStatusLoading] = useState(false);
	const [nukiStatusDevices, setNukiStatusDevices] = useState([]);
	const [nukiStatusError, setNukiStatusError] = useState('');
	const [selectedInstructorProfile, setSelectedInstructorProfile] = useState(null);
	const [showInstructorPhotoPreview, setShowInstructorPhotoPreview] = useState(false);
	const [showQR, setShowQR] = useState(false);
	const [showNukiActionPopup, setShowNukiActionPopup] = useState(false);
	const [nukiActionLoading, setNukiActionLoading] = useState(false);
	const [nukiActionCooldownRemaining, setNukiActionCooldownRemaining] = useState(0);
	const [nukiActionFeedback, setNukiActionFeedback] = useState({type: '', message: ''});
	const [nukiDevice, setNukiDevice] = useState(null);
	const [nukiDeviceLoading, setNukiDeviceLoading] = useState(false);
	const [nukiDistanceMeters, setNukiDistanceMeters] = useState(null);
	const [nukiDistanceLoading, setNukiDistanceLoading] = useState(false);
	const [nukiDistanceError, setNukiDistanceError] = useState('');
	const [qrData] = useState(usrInfo.promoCodeQR);
	const clientInstructors = Array.isArray(usrInfo.clientInstructors) ? usrInfo.clientInstructors : [];

	const getNukiStatePresentation = (state) => {
		switch (String(state || '').toLowerCase()) {
			case 'locked':
				return {label: 'Zamčeno', className: 'header_nuki-state header_nuki-state--locked'};
			case 'unlocking':
				return {label: 'Odemykání', className: 'header_nuki-state header_nuki-state--unlocking'};
			case 'unlocked':
				return {label: 'Odemčeno', className: 'header_nuki-state header_nuki-state--unlocked'};
			case 'locking':
				return {label: 'Zamykání', className: 'header_nuki-state header_nuki-state--locking'};
			default:
				return {label: String(state || 'unknown').toLowerCase(), className: 'header_nuki-state'};
		}
	};

	const renderNukiState = (state) => {
		const presentation = getNukiStatePresentation(state);

		return <span className={presentation.className}>{presentation.label}</span>;
	};

	const getNukiResultFromTimer = () => {
		try {
			return timerCnt.data_nuki_result ? JSON.parse(timerCnt.data_nuki_result) : null;
		} catch (error) {
			return {success: false, error: 'Neplatný uložený Nuki výsledek'};
		}
	};

	const getNukiStatusDevice = () => {
		const result = getNukiResultFromTimer();
		const devices = Array.isArray(result?.data) ? result.data : [];
		return devices.find((item) => item.isConfigured) || devices[0] || null;
	};

	const getNukiStatusClassName = () => {
		const result = getNukiResultFromTimer();
		const device = getNukiStatusDevice();
		const state = String(device?.state || '').toLowerCase();

		if (!result || result.success === false || !device) {
			return 'header_nuki-status header_nuki-status--error';
		}

		if (state === 'locked') {
			return 'header_nuki-status header_nuki-status--locked';
		}

		if (state === 'unlocking') {
			return 'header_nuki-status header_nuki-status--unlocking';
		}

		if (state === 'unlocked') {
			return 'header_nuki-status header_nuki-status--unlocked';
		}

		if (state === 'locking') {
			return 'header_nuki-status header_nuki-status--locking';
		}

		return 'header_nuki-status header_nuki-status--error';
	};

	const getDistanceBetweenCoordinates = (fromLatitude, fromLongitude, toLatitude, toLongitude) => {
		const earthRadius = 6371000;
		const toRadians = (value) => value * (Math.PI / 180);
		const deltaLatitude = toRadians(toLatitude - fromLatitude);
		const deltaLongitude = toRadians(toLongitude - fromLongitude);
		const latitude1 = toRadians(fromLatitude);
		const latitude2 = toRadians(toLatitude);
		const haversine = Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) + Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(deltaLongitude / 2) * Math.sin(deltaLongitude / 2);

		return 2 * earthRadius * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
	};

	const getNukiErrorMessage = (error, fallbackMessage) => {
		const backendMessage = error?.response?.data?.error;

		if (backendMessage) {
			return backendMessage;
		}

		if (error?.response?.status === 403) {
			return 'Toto zařízení není autorizované pro ovládání dveří. Kontaktuj mě kvůli resetu přístupu na telefonním čísle uvedeném níže.';
		}

		return error?.message || fallbackMessage;
	};

	const getCurrentPosition = () =>
		new Promise((resolve, reject) => {
			if (!navigator.geolocation) {
				reject(new Error('Geolokace není v prohlížeči dostupná.'));
				return;
			}

			navigator.geolocation.getCurrentPosition(
				(position) => {
					resolve({
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
					});
				},
				(error) => {
					switch (error.code) {
						case error.PERMISSION_DENIED:
							reject(new Error('Povol přístup k poloze, aby bylo možné ověřit vzdálenost od zámku.'));
							break;
						case error.POSITION_UNAVAILABLE:
							reject(new Error('Polohu telefonu se nepodařilo zjistit.'));
							break;
						case error.TIMEOUT:
							reject(new Error('Zjištění polohy telefonu vypršelo.'));
							break;
						default:
							reject(new Error('Polohu telefonu se nepodařilo zjistit.'));
					}
				},
				{enableHighAccuracy: true, timeout: 10000, maximumAge: 5000},
			);
		});

	useEffect(() => {
		if (nukiActionCooldownRemaining <= 0) {
			return undefined;
		}

		const timerId = window.setInterval(() => {
			setNukiActionCooldownRemaining((prevValue) => {
				if (prevValue <= 1) {
					window.clearInterval(timerId);
					return 0;
				}

				return prevValue - 1;
			});
		}, 1000);

		return () => {
			window.clearInterval(timerId);
		};
	}, [nukiActionCooldownRemaining]);

	useEffect(() => {
		if (!showNukiActionPopup) {
			return undefined;
		}

		let isCancelled = false;
		const loadConfiguredNukiDevice = () => {
			return post('getNukiSmartlock', {}).then((res) => {
				const devices = Array.isArray(res.data) ? res.data : [];
				const configuredDevice = devices.find((item) => item.isConfigured);

				if (!configuredDevice) {
					throw new Error('V nastavení není uložen platný Nuki zámek nebo nebyl v odpovědi nalezen.');
				}

				return configuredDevice;
			});
		};

		setNukiActionFeedback({type: '', message: ''});
		setNukiDevice(null);
		setNukiDistanceMeters(null);
		setNukiDistanceError('');
		setNukiDeviceLoading(true);
		setNukiDistanceLoading(true);

		const loadNukiPopupData = async () => {
			const deviceResult = await loadConfiguredNukiDevice();

			if (isCancelled) {
				return;
			}

			setNukiDeviceLoading(false);
			setNukiDevice(deviceResult);

			const locationResult = await getCurrentPosition();

			if (isCancelled) {
				return;
			}

			setNukiDistanceLoading(false);

			if (deviceResult.latitude === null || deviceResult.longitude === null) {
				setNukiDistanceError('U vybraného zámku chybí GPS souřadnice.');
				return;
			}

			const distance = getDistanceBetweenCoordinates(locationResult.latitude, locationResult.longitude, deviceResult.latitude, deviceResult.longitude);

			setNukiDistanceMeters(distance);
		};

		loadNukiPopupData().catch((error) => {
			if (isCancelled) {
				return;
			}

			console.error(error);
			setNukiDeviceLoading(false);
			setNukiDistanceLoading(false);
			setNukiDistanceError(getNukiErrorMessage(error, 'Nepodařilo se ověřit vzdálenost od zámku.'));
		});

		return () => {
			isCancelled = true;
		};
	}, [showNukiActionPopup]);

	useEffect(() => {
		let cM = parseInt(timerCnt.data_msg_cnt) || 0;
		let cT = parseInt(timerCnt.data_trans_cnt) || 0;
		let cE = parseInt(timerCnt.data_todayeshop_cnt) || 0;

		let titl = ' (';

		if (cM > 0) {
			if (titl !== ' (') {
				titl += ',';
			}
			titl += cM;
		}

		if (usrInfo.sysAdmin === 1 || usrInfo.manager === 1) {
			if (cT > 0) {
				if (titl !== ' (') {
					titl += ',';
				}
				titl += cT;
			}

			if (cE > 0) {
				if (titl !== ' (') {
					titl += ',';
				}
				titl += cE;
			}
		}

		if (titl !== ' (') {
			titl += ')';
		} else {
			titl = '';
		}

		document.title = 'Progress Gym' + titl;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timerCnt]);

	const logout = () => {
		post('logout', {})
			.then((res) => {
				if (res.usrId > -1) {
					localStorage.removeItem('token');
					window.location.pathname = '/';
				}
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const logoutAsUser = () => {
		post('logoutAsUser', {loginAsToken: usrInfo.loginAsToken})
			.then((res) => {
				//localStorage.removeItem('token');
				window.location.pathname = '/';
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const closeQR = () => {
		setShowQR(false);
	};

	const closeInstructorProfile = () => {
		setShowInstructorPhotoPreview(false);
		setSelectedInstructorProfile(null);
	};

	const closeInstructorPhotoPreview = () => {
		setShowInstructorPhotoPreview(false);
	};

	const closeNukiStatusPopup = () => {
		setShowNukiStatusPopup(false);
	};

	const openNukiStatusPopup = () => {
		if (!isSysAdmin || nukiStatusLoading) {
			return;
		}

		setNukiStatusLoading(true);
		setNukiStatusError('');
		setNukiStatusDevices([]);

		post('getNukiSmartlock', {})
			.then((res) => {
				setNukiStatusDevices(Array.isArray(res.data) ? res.data : []);
				setShowNukiStatusPopup(true);
				setNukiStatusLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setNukiStatusError(getNukiErrorMessage(error, 'Nepodařilo se načíst Nuki data'));
				setNukiStatusDevices([]);
				setShowNukiStatusPopup(true);
				setNukiStatusLoading(false);
			});
	};

	const openInstructorProfile = (instructor) => {
		if (!instructor) {
			return;
		}

		setSelectedInstructorProfile({
			id: instructor.ID || 0,
			usrName: instructor.DISPLAY_NAME || '',
			firstName: instructor.FIRSTNAME || '',
			lastName: instructor.LASTNAME || '',
			nickname: instructor.NICKNAME || '',
			phone: instructor.PHONES || '',
			phoneLink: String(instructor.PHONE || '').replace(/\s+/g, ''),
			email: instructor.EMAIL || '',
			profilePhotoUrl: instructor.PROFILE_PHOTO_URL || '',
		});
	};

	const isManagement = usrInfo.sysAdmin === 1 || usrInfo.manager === 1;
	const isSysAdmin = usrInfo.sysAdmin === 1;
	const isInstructor = usrInfo.instructor === 1;
	const isMember = usrInfo.clubMember === 1;
	const canUseNukiAction = isMember || isSysAdmin || usrInfo.manager === 1;
	const currentPath = window.location.pathname;
	const defaultLandingPaths = ['/', '/home', '/login'];
	const homeActivePaths = isMember ? defaultLandingPaths : ['/'];
	const exerciseActivePaths = isMember ? ['/exercise'] : [...defaultLandingPaths, '/exercise'];
	const isPathActive = (activePaths) => activePaths.some((path) => currentPath === path || (path !== '/' && currentPath.startsWith(`${path}/`)));

	const getMenuItemClassName = (iconClass, activePaths) => {
		return `usermenu_item ${iconClass}${isPathActive(activePaths) ? ' active' : ''}`;
	};

	const getHeaderIconClassName = (iconClass, activePaths, extraClassName = '') => {
		const isActive = isPathActive(activePaths);
		return `icon ${iconClass}${isActive ? ' is-active' : ''}${extraClassName ? ` ${extraClassName}` : ''}`;
	};

	const closeNukiActionPopup = () => {
		setShowNukiActionPopup(false);
		setNukiActionFeedback({type: '', message: ''});
		setNukiDevice(null);
		setNukiDistanceMeters(null);
		setNukiDistanceError('');
		setNukiDeviceLoading(false);
		setNukiDistanceLoading(false);
	};

	const handleNukiAction = (action) => {
		if (!canUseNukiAction) {
			setNukiActionFeedback({type: 'error', message: 'Na ovládání Nuki zámku nemáš oprávnění.'});
			return;
		}

		if (nukiActionLoading || nukiActionCooldownRemaining > 0) {
			return;
		}

		setNukiActionLoading(true);
		setNukiActionCooldownRemaining(nukiActionCooldownSeconds);
		setNukiActionFeedback({type: '', message: ''});

		post('saveNukiSmartlockAction', {
			ACTION: action,
			NUKI_DISTANCE_METERS: nukiDistanceMeters !== null ? Math.round(nukiDistanceMeters) : null,
		})
			.then(() => {
				setNukiActionFeedback({
					type: 'success',
					message: action === 'unlock' ? 'Dveře byly odeslány k odemknutí.' : 'Dveře byly odeslány k zamknutí.',
				});
				setNukiActionLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setNukiActionFeedback({
					type: 'error',
					message: getNukiErrorMessage(error, 'Nepodařilo se provést Nuki akci.'),
				});
				setNukiActionLoading(false);
			});
	};

	const nukiPopupBusy = nukiDeviceLoading || nukiDistanceLoading;
	const isNukiWithinAllowedDistance = nukiDistanceMeters !== null && nukiDistanceMeters <= nukiDistanceLimitMeters;
	const isNukiActionDisabled = !canUseNukiAction || nukiActionLoading || nukiActionCooldownRemaining > 0 || nukiPopupBusy || !nukiDevice || (!isSysAdmin && !isNukiWithinAllowedDistance);
	const formattedNukiCooldown = `${String(Math.floor(nukiActionCooldownRemaining / 60)).padStart(2, '0')}:${String(nukiActionCooldownRemaining % 60).padStart(2, '0')}`;
	const formattedNukiDistance = nukiDistanceMeters !== null ? `${Math.round(nukiDistanceMeters)}m` : '--';

	const instructorPopupProfile = selectedInstructorProfile ? (
		<div className="header_trainer-profile">
			<button
				type="button"
				className={`header_trainer-profile-media${selectedInstructorProfile.profilePhotoUrl ? ' is-clickable' : ''}`}
				onClick={() => {
					if (selectedInstructorProfile.profilePhotoUrl) {
						setShowInstructorPhotoPreview(true);
					}
				}}
				aria-label={selectedInstructorProfile.profilePhotoUrl ? 'Zobrazit zvětšenou profilovou fotku trenéra' : 'Profilová fotka trenéra'}
			>
				{selectedInstructorProfile.profilePhotoUrl ? <img className="header_trainer-profile-image" src={selectedInstructorProfile.profilePhotoUrl} alt={selectedInstructorProfile.usrName || 'Trenér'} /> : <div className="header_trainer-profile-fallback">{getProfileInitials(selectedInstructorProfile.usrName || `${selectedInstructorProfile.firstName} ${selectedInstructorProfile.lastName}`) || 'T'}</div>}
			</button>
			<div className="header_trainer-profile-content">
				<div className="header_trainer-profile-name">
					{selectedInstructorProfile.firstName} {selectedInstructorProfile.lastName} {selectedInstructorProfile.nickname !== '' ? `(${selectedInstructorProfile.nickname})` : ''}
				</div>
				{selectedInstructorProfile.email !== '' ? (
					<a className="header_trainer-profile-link" href={`mailto:${selectedInstructorProfile.email}`}>
						{selectedInstructorProfile.email}
					</a>
				) : (
					''
				)}
				{selectedInstructorProfile.phone !== '' ? (
					<a className="header_trainer-profile-link" href={`tel:${selectedInstructorProfile.phoneLink}`}>
						{selectedInstructorProfile.phone}
					</a>
				) : (
					''
				)}
			</div>
		</div>
	) : null;

	const instructorStrip =
		clientInstructors.length > 0 ? (
			<div className="header_client-strip" aria-label="Tvoji instruktoři">
				<div className="header_client-strip-track">
					{clientInstructors.map((item) => {
						const displayName = item.DISPLAY_NAME || '';
						const initials = getProfileInitials(displayName) || 'T';

						return (
							<button type="button" className="header_client-avatar" key={item.ID || displayName} title={displayName} aria-label={displayName} onClick={() => openInstructorProfile(item)}>
								{item.PROFILE_PHOTO_URL ? <img className="header_client-avatar-image" src={item.PROFILE_PHOTO_URL} alt={displayName} /> : <span className="header_client-avatar-initials">{initials}</span>}
							</button>
						);
					})}
				</div>
			</div>
		) : null;

	const renderHeaderLink = (path, key, iconClass, activePaths, extraClassName = '', children = null, options = {}) => {
		const isActive = isPathActive(activePaths);

		return (
			<Link
				to={path}
				key={key}
				onClick={(event) => {
					if (isActive) {
						if (typeof options.onActiveClick === 'function') {
							event.preventDefault();
							options.onActiveClick();
							return;
						}

						if (options.disableActiveClick) {
							event.preventDefault();
						}
					}
				}}
			>
				<div className={getHeaderIconClassName(iconClass, activePaths, extraClassName)}>{children}</div>
			</Link>
		);
	};

	const outdoorTempElement =
		timerCnt?.data_outdoor_temp != null ? (
			<Link to="/temperature" key="outdoor_temp" className="header_outdoor-temp-link">
				<div className={`header_outdoor-temp header_action cr_p${isPathActive(['/temperature']) ? ' is-active' : ''}`}>
					{String(timerCnt.data_outdoor_temp)
						.split('\n')
						.map((temp, index) => (
							<div className="header_outdoor-temp-line" key={index}>
								{temp}
							</div>
						))}
				</div>
			</Link>
		) : null;

	const primaryHeaderActions = [];

	if (typeof usrInfo.loginAsToken !== 'undefined' && usrInfo.loginAsToken !== '') {
		primaryHeaderActions.push(<div className="icon logout header_action" key="lnk_logout_as" onClick={logoutAsUser} alt="" />);
	}

	if (outdoorTempElement) {
		primaryHeaderActions.push(outdoorTempElement);
	}

	if (usrInfo.clubMember === 1) {
		primaryHeaderActions.push(
			renderHeaderLink('/', 'lnk_home', 'keyon', homeActivePaths, 'header_action', null, {
				onActiveClick: () => setShowNukiActionPopup(true),
				disableActiveClick: true,
			}),
		);
	}

	primaryHeaderActions.push(
		renderHeaderLink(
			'/lesson',
			'lnk_lesson',
			'lesson',
			['/lesson'],
			'header_action txt_c',
			timerCnt.data_lesson_cnt > 0 ? (
				<span className="header_icon-count fnt_message fnt_b fnt_13">
					<b>{timerCnt.data_lesson_cnt}</b>
				</span>
			) : null,
		),
	);

	primaryHeaderActions.push(renderHeaderLink('/exercise', 'lnk_exercise', 'exercise', exerciseActivePaths, 'header_action'));

	if (usrInfo.clubMember === 1) {
		primaryHeaderActions.push(renderHeaderLink('/eshop', 'lnk_eshop', 'eshop', ['/eshop'], 'header_action'));
		primaryHeaderActions.push(renderHeaderLink('/pay', 'lnk_pay', 'pay', ['/pay'], 'header_action'));
		primaryHeaderActions.push(renderHeaderLink('/rule', 'lnk_rule', 'rule', ['/rule'], 'header_action'));
	}

	primaryHeaderActions.push(renderHeaderLink('/calendarevents', 'lnk_calendar', 'calendaric', ['/calendarevents'], 'header_action'));

	const secondaryHeaderActions = [];

	if (usrInfo.sysAdmin === 1 || usrInfo.manager === 1) {
		const nukiStatusDevice = getNukiStatusDevice();
		const batteryCharge = nukiStatusDevice?.batteryCharge;

		secondaryHeaderActions.push(
			<button type="button" className={`${getNukiStatusClassName()} header_action ${usrInfo.sysAdmin === 1 ? 'is-clickable' : ''}`} key="nuki_status" onClick={openNukiStatusPopup} aria-label="Stav Nuki zámku" title="Stav Nuki zámku">
				<span className="header_nuki-status-icon" />
				{batteryCharge !== undefined && batteryCharge !== null ? <span className="header_icon-count fnt_message fnt_1">{batteryCharge}%</span> : <span className="header_nuki-status-alert">!</span>}
			</button>,
		);
	}

	if (usrInfo.sysAdmin === 1) {
		secondaryHeaderActions.push(renderHeaderLink('/userlog', 'lnk_userlog', 'userlog', ['/userlog'], 'header_action'));
		secondaryHeaderActions.push(renderHeaderLink('/user', 'lnk_user', 'user', ['/user'], 'header_action'));
	}

	if (usrInfo.sysAdmin === 1 || usrInfo.manager === 1) {
		secondaryHeaderActions.push(renderHeaderLink('/member', 'lnk_member', 'member', ['/member'], 'header_action'));
		secondaryHeaderActions.push(
			renderHeaderLink(
				'/eshopsysadmin',
				'lnk_eshop_sysadmin',
				'eshop_sysadmin',
				['/eshopsysadmin'],
				'header_action txt_c',
				(usrInfo.sysAdmin === 1 || usrInfo.manager === 1) && timerCnt.data_todayeshop_cnt > 0 ? (
					<span className="header_icon-count fnt_message fnt_b fnt_13">
						<b>{timerCnt.data_todayeshop_cnt}</b>
					</span>
				) : null,
			),
		);
		secondaryHeaderActions.push(renderHeaderLink('/payment', 'lnk_payment', 'payment_sysadmin', ['/payment'], 'header_action'));
		secondaryHeaderActions.push(
			renderHeaderLink(
				'/trans',
				'lnk_trans',
				'trans_sysadmin',
				['/trans'],
				'header_action txt_c',
				(usrInfo.sysAdmin === 1 || usrInfo.manager === 1) && timerCnt.data_trans_cnt > 0 ? (
					<span className="header_icon-count fnt_message fnt_b fnt_13">
						<b>{timerCnt.data_trans_cnt}</b>
					</span>
				) : null,
			),
		);
	}

	if (usrInfo.instructor === 1) {
		secondaryHeaderActions.push(renderHeaderLink('/instructorusr', 'lnk_instructorusr', 'instructorusr', ['/instructorusr'], 'header_action'));
		secondaryHeaderActions.push(renderHeaderLink('/instructorhome', 'lnk_instructorhome', 'instructorhome', ['/instructorhome'], 'header_action'));
	}

	const menuSections = [
		isMember
			? {
					title: 'Členství',
					items: [
						{path: '/', activePaths: homeActivePaths, key: 'lnk_home_usr', iconClass: 'home', label: 'Rezervace'},
						{path: '/eshop', activePaths: ['/eshop'], key: 'lnk_eshop_usr', iconClass: 'eshop', label: 'Moje nákupy'},
						{path: '/mypayment', activePaths: ['/mypayment'], key: 'lnk_pay_usr', iconClass: 'permanent', label: 'Moje platby'},
						{path: '/mybonus', activePaths: ['/mybonus'], key: 'lnk_bonus_usr', iconClass: 'bonus', label: 'Moje pozvánky a bonusy'},
						{path: '/message', activePaths: ['/message'], key: 'lnk_message_usr', iconClass: 'email', label: 'Zprávy'},
					],
				}
			: null,
		isInstructor
			? {
					title: 'Správa trenéra',
					items: [
						{path: '/instructorhome', activePaths: ['/instructorhome'], key: 'lnk_instructorhome_usr', iconClass: 'instructorhome', label: 'Moje lekce'},
						{path: '/instructorusr', activePaths: ['/instructorusr'], key: 'lnk_instructorusr_usr', iconClass: 'instructorusr', label: 'Moji klienti'},
						{path: '/lesson', activePaths: ['/lesson'], key: 'lnk_home_lesson', iconClass: 'lesson', label: 'Lekce'},
					],
				}
			: null,
		{
			title: 'Trénink',
			items: [
				{path: '/exercise', activePaths: exerciseActivePaths, key: 'lnk_exercise_usr', iconClass: 'exercise', label: 'Výkony'},
				{path: '/workoutmax', activePaths: ['/workoutmax'], key: 'lnk_workoutmax_usr', iconClass: 'workoutmax', label: 'Maximálky'},
				{path: '/timer', activePaths: ['/timer'], key: 'lnk_timer_usr', iconClass: 'timer', label: 'Časovač'},
				{path: '/workout', activePaths: ['/workout'], key: 'lnk_workout_usr', iconClass: 'workout', label: 'Cviky'},
				{path: '/musclegroup', activePaths: ['/musclegroup'], key: 'lnk_musclegroup_usr', iconClass: 'musclegroup', label: 'Svalové partie'},
				{path: '/stat', activePaths: ['/stat'], key: 'lnk_stat_usr', iconClass: 'stat', label: 'Statistiky a grafy'},
			],
		},
		{
			title: 'Profil a nastavení',
			items: [
				{path: '/calendarevents', activePaths: ['/calendarevents'], key: 'lnk_calendar_usr', iconClass: 'calendarmn', label: 'Kalendář'},
				{path: '/links', activePaths: ['/links'], key: 'lnk_links_usr', iconClass: 'links', label: 'Odkazy'},
				{path: '/note', activePaths: ['/note'], key: 'lnk_note_usr', iconClass: 'note', label: 'Poznámky'},
				{path: '/setting', activePaths: ['/setting'], key: 'lnk_setting_usr', iconClass: 'setting', label: 'Nastavení'},
			],
		},
		isManagement
			? {
					title: 'Správa klubu',
					items: [
						{path: '/trans', activePaths: ['/trans'], key: 'lnk_trans_usr', iconClass: 'trans', label: 'Transakce'},
						{path: '/payment', activePaths: ['/payment'], key: 'lnk_payment_usr', iconClass: 'payment', label: 'Platby'},
						{path: '/eshopsysadmin', activePaths: ['/eshopsysadmin'], key: 'lnk_eshop_sysadmin_usr', iconClass: 'eshop_sysadmin', label: 'Nákupy'},
						{path: '/member', activePaths: ['/member'], key: 'lnk_member_usr', iconClass: 'member', label: 'Členové'},
					],
				}
			: null,
		isSysAdmin
			? {
					title: 'System',
					items: [
						{path: '/user', activePaths: ['/user'], key: 'lnk_user_usr', iconClass: 'user', label: 'Uživatelé'},
						//{path: '/keys', activePaths: ['/keys'], key: 'lnk_key_usr', iconClass: 'keys', label: 'Klíče'},
						{path: '/messagesysadmin', activePaths: ['/messagesysadmin'], key: 'lnk_messagesysadmin_usr', iconClass: 'messagesysadmin', label: 'Zprávy'},
						{path: '/userlog', activePaths: ['/userlog'], key: 'lnk_log_usr', iconClass: 'userlog', label: 'Logy'},
						{path: '/eshopitem', activePaths: ['/eshopitem'], key: 'lnk_eshopitem_usr', iconClass: 'eshopitem', label: 'Zboží'},
						{path: '/workoutoffic', activePaths: ['/workoutoffic'], key: 'lnk_workoutoffic_usr', iconClass: 'worknoutsysadmin', label: 'Oficiální cviky'},
					],
				}
			: null,
	].filter(Boolean);

	return (
		<div className={`header ${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''} pos_r`}>
			<ConfirmPopup showConfirmPopup={showQR} closeConfirmPopup={closeQR} infoHeader="Moje pozvánka" infoMain="">
				<div
					className="cr_p"
					style={{backgroundColor: '#ffffff', padding: '10px', display: 'inline-block'}}
					onClick={() => {
						clipboard(usrInfo.promoCodeQR);
					}}
					dangerouslySetInnerHTML={{__html: qrData}}
				></div>
				<div className="zero h1" />
				<span
					onClick={() => {
						clipboard(usrInfo.promoCodeQR);
					}}
				>
					{usrInfo.myPromoCode}
				</span>
				<div className="zero h0" />
			</ConfirmPopup>

			<ConfirmPopup showConfirmPopup={selectedInstructorProfile !== null} closeConfirmPopup={closeInstructorProfile} infoHeader="Můj trenér" infoMain="">
				{instructorPopupProfile}
			</ConfirmPopup>

			<ConfirmPopup showConfirmPopup={showInstructorPhotoPreview} closeConfirmPopup={closeInstructorPhotoPreview} infoHeader="Profilová fotka trenéra" infoMain="" popupContentClassName="usrprofile_preview_popup_content" bodyClassName=" usrprofile_preview_popup_body popup_content_in_body--hidden-scrollbar" bodyInnerClassName=" usrprofile_preview_popup_body_in">
				<div className="usrprofile_preview_stage">
					<div className="usrprofile_preview_circle">{selectedInstructorProfile?.profilePhotoUrl ? <img className="usrprofile_preview_image" src={selectedInstructorProfile.profilePhotoUrl} alt={selectedInstructorProfile.usrName || 'Trenér'} /> : <div className="usrprofile_preview_initials">{getProfileInitials(selectedInstructorProfile?.usrName || `${selectedInstructorProfile?.firstName || ''} ${selectedInstructorProfile?.lastName || ''}`) || 'T'}</div>}</div>
				</div>
			</ConfirmPopup>

			<ConfirmPopup showConfirmPopup={showNukiStatusPopup} closeConfirmPopup={closeNukiStatusPopup} infoHeader="Nuki odpověď" infoMain="">
				<div className="header_nuki-response">
					{nukiStatusError !== '' ? <div className="fnt_red mb_1">{nukiStatusError}</div> : null}
					{nukiStatusDevices.length > 0 ? (
						<div className="header_nuki-response-list">
							{nukiStatusDevices.map((device, deviceIndex) => (
								<div className="header_nuki-response-device" key={`${device.smartlockId}-${deviceIndex}`}>
									<div className="header_nuki-response-device-name">{device.name || 'Bez názvu'}</div>
									<div className="header_nuki-response-grid">
										<div className="header_nuki-response-row">
											<span className="header_nuki-response-label">smartlockId:</span>
											<span className="header_nuki-response-value">{device.smartlockId}</span>
											<button type="button" className="header_nuki-response-copy" onClick={() => clipboard(device.smartlockId)} aria-label="Kopírovat smartlockId">
												<img className="icon1 copy" alt="" />
											</button>
										</div>
										<div className="header_nuki-response-row">
											<span className="header_nuki-response-label">state:</span> {renderNukiState(device.state)}
										</div>
										<div className="header_nuki-response-row">
											<span className="header_nuki-response-label">batteryCharge:</span> {device.batteryCharge !== null ? `${device.batteryCharge}%` : 'N/A'}
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="header_nuki-response-empty">Bez zařízení.</div>
					)}
				</div>
			</ConfirmPopup>

			<ConfirmPopup showConfirmPopup={showNukiActionPopup} closeConfirmPopup={closeNukiActionPopup} infoHeader="Ovládání dveří" infoMain="">
				<div className="header_nuki-popup">
					<div className="header_nuki-popup-status">
						{nukiDevice?.name ? <div className="header_nuki-popup-device-name">{nukiDevice.name}</div> : null}
						<div>{nukiDevice ? renderNukiState(nukiDevice.state) : <span className="fnt_12 header_nuki-popup-status-text">{nukiPopupBusy ? 'Načítám stav...' : 'Stav není dostupný'}</span>}</div>
						<div className="fnt_12 header_nuki-popup-distance">Vzdálenost od zámku: {nukiPopupBusy ? 'zjišťuji...' : formattedNukiDistance}</div>
						{nukiDistanceError !== '' ? <div className="fnt_12 fnt_red header_nuki-popup-error">{nukiDistanceError}</div> : !isSysAdmin && nukiDistanceMeters !== null && nukiDistanceMeters > nukiDistanceLimitMeters ? <div className="fnt_12 fnt_red header_nuki-popup-error">Pro ovládání zámku musíš být do {nukiDistanceLimitMeters}m od zámku.</div> : null}
					</div>

					<div className="header_nuki-popup-actions">
						<button type="button" className="button green header_nuki-popup-button" onClick={() => handleNukiAction('unlock')} disabled={isNukiActionDisabled}>
							Odemknout
						</button>
						<button type="button" className="button red header_nuki-popup-button" onClick={() => handleNukiAction('lock')} disabled={isNukiActionDisabled}>
							Zamknout
						</button>
					</div>
					{nukiActionCooldownRemaining > 0 ? <div className="fnt_12 header_nuki-popup-cooldown">Tlačítka se odblokují za {formattedNukiCooldown}</div> : null}
				</div>
				{nukiActionFeedback.message !== '' ? <div className={nukiActionFeedback.type === 'error' ? 'fnt_red header_nuki-popup-feedback' : 'fnt_green header_nuki-popup-feedback'}>{nukiActionFeedback.message}</div> : null}
				<div className="header_nuki-popup-note">
					Z bezpečnostních důvodů je použití zámku možné pouze ze vzdálenosti maximálně {nukiDistanceLimitMeters}m, proto je nutné mít pro web progressgym.eu povolenou GPS polohu. V případě problému s ovládáním zámku mě kontaktuj
					<br />
					na telefonním čísle{' '}
					<a href="tel:605359072" className="header_nuki-popup-note-link">
						605 359 072
					</a>
					.<br />
					Na iPhonu a Safari je možné toto vyřešit v nastavení 'Soukromí a zabezpečení' - 'Polohové služby' - 'Stránky v Safari' - zapnout 'Při používání aplikace' a 'Přesná poloha'.
				</div>
			</ConfirmPopup>

			{loading || nukiActionLoading ? <div className="loading" /> : ''}

			<div className="header_shell">
				<div className="header_row header_row--primary">
					{instructorStrip}

					<div className="header_actions header_actions--primary">
						{primaryHeaderActions}

						<div className="icon menu usermenu_wrap header_action" id="iconmenu" role="button" aria-label="Otevřít hlavní menu" aria-expanded="false">
							<div className="usermenu_trigger">
								<span className="usermenu_trigger_line" />
								<span className="usermenu_trigger_line" />
								<span className="usermenu_trigger_line" />
							</div>
							<div className="usermenu hidden">
								<div className="usermenu_headerblock">
									<div className="usermenu_headercopy">Progress Gym</div>
									<div className="usermenu_email" onClick={() => clipboard(usrInfo.email)}>
										{usrInfo.email}
									</div>
								</div>

								<div className="usermenu_meta">
									{/*
						<div className={`usermenu_doorkey usermenu_meta_item ${usrInfo.doorKey !== '' ? 'is-active fnt_green' : 'is-empty fnt_orange'}`} onClick={() => usrInfo.doorKey !== '' ? clipboard(usrInfo.doorKey) : null}>
							<span className="usermenu_meta_label">Klíč</span>
							<span className="usermenu_meta_value">{usrInfo.doorKey !== '' ? usrInfo.doorKey : 'žádný'}</span>
						</div>
						*/}

									{usrInfo.myPromoCode !== '' ? (
										<div className="usermenu_meta_row">
											<div className="usermenu_doorkey usermenu_meta_item usermenu_meta_item--invite is-active fnt_green" onClick={() => clipboard(usrInfo.myPromoCode)}>
												<span className="usermenu_meta_label">Pozvánka</span>
												<span className="usermenu_meta_value">{usrInfo.myPromoCode}</span>
											</div>
											<button type="button" className="usermenu_qrbutton" onClick={() => setShowQR(true)}>
												QR
											</button>
										</div>
									) : (
										<div className="usermenu_doorkey usermenu_meta_item is-empty fnt_orange">
											<span className="usermenu_meta_label">Pozvánka</span>
											<span className="usermenu_meta_value">žádná</span>
										</div>
									)}
								</div>

								<div className="usermenu_sections">
									{menuSections.map((section) => (
										<div className="usermenu_section" key={section.title}>
											<div className="usermenu_section_title">{section.title}</div>
											{section.items.map((item) => (
												<Link to={item.path} key={item.key} className={getMenuItemClassName(item.iconClass, item.activePaths)}>
													{item.label}
												</Link>
											))}
										</div>
									))}
								</div>

								<button type="button" className="usermenu_item logout usermenu_logout" onClick={logout}>
									Odhlásit
								</button>
							</div>
						</div>
					</div>
				</div>

				{secondaryHeaderActions.length > 0 ? (
					<div className="header_row header_row--secondary">
						<div className="header_actions header_actions--secondary">{secondaryHeaderActions}</div>
					</div>
				) : null}
			</div>
		</div>
	);
}
